import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import type { CatalogProducts } from '@vy/types';
import { VyIcon } from '@vy/ui-design-system';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useMenu } from '../../../../../hooks/use-menu';
import { runMutation } from '../../../../../utils/vy-mutation';
import { invalidateProductsQueries } from '../../../../../utils/query-invalidation';
import { useDeleteCatalogProducts } from '../../../../../hooks/requests/use-delete-catalog-products';
import { UpsertCatalogProductsDialog } from '../CreateProductDialog/UpsertCatalogProductsDialog';
import { useToggle } from '../../../../../hooks/use-toggle';

type CatalogProductsTableActionsProps = {
  catalogProductId: string;
  row: CatalogProducts;
  disableDelete: boolean;
  catalogProductName?: string;
};

export const CatalogProductsTableActions: React.FC<CatalogProductsTableActionsProps> = ({
  catalogProductId,
  row,
  disableDelete,
  catalogProductName,
}) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const { anchorElement, isOpen, handleMenuOpen, handleMenuClose } = useMenu();
  const [isEditDialogOpen, toggleIsEditDialogOpen] = useToggle(false);

  const queryClient = useQueryClient();

  const deleteCatalogProductMutation = useDeleteCatalogProducts({
    onSuccess: () => invalidateProductsQueries(queryClient),
  });

  const handleEditClick = () => {
    handleMenuClose();
    toggleIsEditDialogOpen();
  };

  const handleEditDialogClose = () => {
    toggleIsEditDialogOpen();
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    handleOpen();
  };

  const handleDelete = async () => {
    await runMutation(
      deleteCatalogProductMutation,
      { catalogProductId },
      {
        pending: 'Deleting catalog product...',
        success: 'Catalog product successfully deleted',
        error: 'Failed to delete catalog product',
      },
      {
        onSuccess: async () => {
          await invalidateProductsQueries(queryClient);
        },
      },
    );
  };

  return (
    <>
      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        label={`Delete ${catalogProductName}`}
        labelDescription="Are you sure you want to delete this product?"
        ctaText="Delete"
        warningText="This action is final and cannot be reversed"
        cancelButtonText="Cancel"
        onSubmit={handleDelete}
        disableDividers={true}
      />

      <UpsertCatalogProductsDialog
        openCreateProductDialog={isEditDialogOpen}
        onClose={handleEditDialogClose}
        editProduct={row}
      />

      <IconButton size="medium" onClick={handleMenuOpen}>
        <VyIcon icon="DotsMenu" style={{ transform: 'rotate(90deg)' }} />
      </IconButton>

      <Menu
        anchorEl={anchorElement}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={isOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <VyIcon icon="Edit" style={{ transform: 'scale(0.8)' }} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleDeleteClick} disabled={disableDelete}>
          <ListItemIcon>
            <VyIcon icon="Delete" style={{ transform: 'scale(0.8)' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
